import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { useStoreState } from '@chess/api-model/react';
import { useSelectedGame } from '@chess/api-navigation/react';
import '@chess/api-insights/charts.css';
import { GameBar } from './GameBar.tsx';
import { GameStrip } from './GameStrip.tsx';
import { useVerdict } from '@chess/api-verdict/react';

// The game bar's container, extension `shared.gameBar`: the dashboard
// screens (Home, Openings, Middlegame, Ending) place it under their 30-day
// band, over their per-game sections, which follow the same selection
// (`?game=`, read and changed through `@chess/api-navigation`'s
// `useSelectedGame`). Nothing while the period holds no games. Home places
// the game strip instead (`shared.gameStrip`, 2026-10-08): the same
// selection as a row of tiles, one per game.
export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  const Bar: React.FC = () => {
    const { state } = useStoreState(getApi);
    const selected = useSelectedGame(getApi);
    if (state?.status === 'failed') return null;
    if (state?.status !== 'ready') return <GameBar loading />;
    if (!selected.game) return null;
    return <GameBar games={selected.games} index={selected.index} onStep={selected.step} onJump={selected.jump} />;
  };
  app.registerExtension('shared.gameBar', Bar);

  const Strip: React.FC = () => {
    const { state } = useStoreState(getApi);
    const selected = useSelectedGame(getApi);
    const verdict = useVerdict(getApi);
    // Each tile's deciding stage, from the verdict API; recomputed when the games change.
    const games = React.useMemo(
      () => (verdict ? selected.games.map((g) => ({ ...g, deciding: verdict.decidingStage(g) })) : undefined),
      [verdict, selected.games],
    );
    if (state?.status === 'failed') return null;
    if (state?.status !== 'ready' || !games) return <GameStrip loading />;
    if (!selected.game) return null;
    return <GameStrip games={games} index={selected.index} onSelect={selected.select} onStep={selected.step} onJump={selected.jump} libraryHref="/library" />;
  };
  app.registerExtension('shared.gameStrip', Strip);
}
